import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, formatDateId } from '../api/client';
import type { DraftSummary } from '../api/types';
import { Alert, Button, Card, Spinner } from '../components/ui';
import { useT } from '../i18n/locale';
import { useConfirm } from '../components/confirm';

export default function DraftsScreen() {
  const t = useT();
  const { databaseId } = useParams<{ databaseId: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Which draft is mid-delete, so only that row's button reacts. */
  const [removing, setRemoving] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await api.get<{ drafts: DraftSummary[] }>(`/drafts?databaseId=${databaseId}`);
      setDrafts(data.drafts);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('drafts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
     
  }, [databaseId]);

  async function remove(id: string) {
    const draft = drafts.find((d) => d.id === id);
    const ok = await confirm({
      title: t('drafts.confirmTitle'),
      tone: 'danger',
      confirmLabel: t('common.delete'),
      body: t('drafts.confirmBody'),
      details: draft
        ? [
            { label: t('drafts.poNumber'), value: draft.poNumber },
            { label: t('drafts.vendor'), value: draft.vendorName ?? '-' },
            {
              label: t('drafts.scanned'),
              value: t('drafts.scannedValue', { lines: draft.lineCount, qty: draft.totalQty }),
            },
            { label: t('drafts.warehouse'), value: draft.warehouseName },
          ]
        : undefined,
    });
    if (!ok) return;
    setRemoving(id);
    try {
      await api.delete(`/drafts/${id}`);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('drafts.deleteFailed'));
    } finally {
      setRemoving(null);
    }
  }

  if (loading) return <Spinner label={t('drafts.searching')} />;

  return (
    <div className="mx-auto w-full max-w-5xl p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="m-0 text-lg font-bold text-white">{t('drafts.title')}</h2>
        <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-slate-800">
          {drafts.length} Draft
        </span>
      </div>

      {error ? <Alert tone="error">{error}</Alert> : null}

      {drafts.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="m-0 font-bold text-slate-800">{t('drafts.empty')}</p>
          <p className="mt-1 text-sm text-slate-500">
            {t('drafts.emptyHint')}
          </p>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {drafts.map((d) => (
            <Card key={d.id} className="flex flex-col">
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="mb-1 inline-block rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                      {d.databaseAlias}
                    </span>
                    <h3 className="m-0 text-sm font-extrabold text-slate-900">{d.poNumber}</h3>
                  </div>
                  <span className="text-right text-[10px] font-bold text-slate-500">
                    {formatDateId(d.updatedAt)}
                  </span>
                </div>
                <div className="mt-2 space-y-1 text-xs font-medium text-slate-600">
                  <div>{t('drafts.vendorInline', { name: d.vendorName ?? '-' })}</div>
                  <div>
                    {t('drafts.warehouseLabel')} <b className="text-slate-900">{d.warehouseName}</b>
                  </div>
                  <div>{t('drafts.deliveryNoteInline', { number: d.deliveryNoteNo })}</div>
                  <div className="font-bold text-emerald-600">
                    {t('drafts.lineSummary', { lines: d.lineCount, qty: d.totalQty })}
                  </div>
                  <div className="text-slate-400">{t('drafts.byInline', { name: d.createdByName })}</div>
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-3 py-2">
                <button
                  onClick={() => void remove(d.id)}
                  disabled={removing !== null}
                  className="cursor-pointer rounded-lg border-0 bg-transparent px-3 py-1.5 text-[11px] font-bold text-red-500 hover:bg-red-50 disabled:opacity-50"
                >
                  {removing === d.id ? t('drafts.deleting') : t('common.delete')}
                </button>
                <Button
                  onClick={() => navigate(`/db/${databaseId}/scan?draft=${d.id}`)}
                  className="px-4 py-1.5 text-xs"
                >
                  Lanjutkan →
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
